export interface ModelInfo {
  id: string;
  name: string;
  description?: string;
  context_length?: number;
  capabilities?: string[];
  pricing?: {
    input_tokens?: number;
    output_tokens?: number;
  };
}

export interface ProviderModelsResponse {
  providers: Record<string, {
    models: ModelInfo[];
    last_updated: string;
    available: boolean;
  }>;
  last_fetched: string;
}

export interface SingleProviderResponse {
  provider: string;
  models: ModelInfo[];
  last_updated: string;
}

class ModelsService {
  private apiUrl: string;
  private apiKey: string;
  private cache: Map<string, { data: any; timestamp: number }> = new Map();
  private cacheExpiry = 10 * 60 * 1000; // 10 minutes

  constructor() {
    // Use service binding through pages function for secure process-to-process communication
    // This routes through /api/ -> Pages Function -> Service Binding -> Backend Worker
    this.apiUrl = '/api'; // Service binding via pages function
    this.apiKey = ''; // Not needed - authentication handled by service binding
  }

  private async makeRequest(endpoint: string): Promise<any> {
    const response = await fetch(`${this.apiUrl}${endpoint}`, {
      headers: {
        'Content-Type': 'application/json',
        // No X-API-Key needed - authentication handled by service binding
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        'Pragma': 'no-cache'
      },
      cache: 'no-store' // Force bypass all caches
    });

    if (!response.ok) {
      // For 401 errors, return a specific error that can be handled gracefully
      if (response.status === 401) {
        throw new Error(`UNAUTHORIZED: ${response.statusText}`);
      }
      throw new Error(`API request failed: ${response.status} ${response.statusText}`);
    }

    return response.json();
  }

  private getCachedData(key: string): any | null {
    const cached = this.cache.get(key);
    if (cached && Date.now() - cached.timestamp < this.cacheExpiry) {
      return cached.data;
    }
    return null;
  }

  private setCachedData(key: string, data: any): void {
    this.cache.set(key, {
      data,
      timestamp: Date.now()
    });
  }

  async getAllProviderModels(): Promise<ProviderModelsResponse> {
    const cacheKey = 'all-providers';
    const cached = this.getCachedData(cacheKey);
    if (cached) {
      return cached;
    }

    try {
      const response = await this.makeRequest('/v1/models');
      const data = response.data || response;
      
      this.setCachedData(cacheKey, data);
      return data;
    } catch (error: any) {
      console.error('Failed to fetch all provider models:', error);
      
      // Return a default response structure if the API call fails
      const defaultResponse: ProviderModelsResponse = {
        providers: {},
        last_fetched: new Date().toISOString()
      };
      
      // Add each provider with available=false by default
      const providers = ['anthropic', 'openai', 'grok', 'gemini', 'cloudflare', 'mistral', 'deepseek'];
      for (const provider of providers) {
        defaultResponse.providers[provider] = {
          models: this.getFallbackModels(provider),
          last_updated: new Date().toISOString(),
          available: provider === 'cloudflare' // Only Cloudflare is always available
        };
      }
      
      return defaultResponse;
    }
  }

  async getModelsForProvider(provider: string): Promise<ModelInfo[]> {
    const cacheKey = `provider-${provider}`;
    const cached = this.getCachedData(cacheKey);
    if (cached) {
      return cached;
    }

    try {
      const response = await this.makeRequest(`/v1/providers/status`);
      
      const data = response.data || response;
      const providers = data.providers || response.providers || {};
      
      const providerData = providers[provider];
      
      if (providerData && providerData.available_models && Array.isArray(providerData.available_models) && providerData.available_models.length > 0) {
        const availableModels = providerData.available_models;
        
        const models: ModelInfo[] = availableModels.map((modelId: string) => {
          const fallbackModels = this.getFallbackModels(provider);
          const fallbackModel = fallbackModels.find(m => m.id === modelId);
          
          return {
            id: modelId,
            name: fallbackModel?.name || this.formatModelName(modelId),
            description: fallbackModel?.description || this.getModelDescription(provider, modelId),
            capabilities: fallbackModel?.capabilities || this.getModelCapabilities(provider, modelId),
            context_length: fallbackModel?.context_length
          };
        });
        
        this.setCachedData(cacheKey, models);
        return models;
      } else {
        console.log(`No available models found for ${provider} in provider status`);
        return this.getFallbackModels(provider);
      }
    } catch (error: any) {
      if (error.message?.includes('UNAUTHORIZED')) {
        console.log(`Provider ${provider} not configured (missing API key)`);
      } else {
        console.error(`Failed to fetch models for ${provider}:`, error);
      }
      return this.getFallbackModels(provider);
    }
  }

  private formatModelName(modelId: string): string {
    // Convert model IDs to readable names
    return modelId
      .replace(/[@]/g, '')
      .replace(/[/_-]/g, ' ')
      .replace(/\b\w/g, (char) => char.toUpperCase());
  }

  async refreshModelCache(): Promise<void> {
    try {
      await this.makeRequest('/v1/models/refresh');
      this.cache.clear(); // Clear local cache too
    } catch (error) {
      console.error('Failed to refresh model cache:', error);
      throw error;
    }
  }

  private getFallbackModels(provider: string): ModelInfo[] {
    const fallbackModels: Record<string, ModelInfo[]> = {
      anthropic: [
        // Claude 3.5 Series
        { id: 'claude-3-5-sonnet-20241022', name: 'Claude 3.5 Sonnet (Latest)', capabilities: ['text', 'vision', 'tool_use', 'computer_use'], context_length: 200000 },
        
        // Claude 3 Series
        { id: 'claude-3-opus-20240229', name: 'Claude 3 Opus', capabilities: ['text', 'vision'], context_length: 200000 },
        { id: 'claude-3-sonnet-20240229', name: 'Claude 3 Sonnet', capabilities: ['text', 'vision'], context_length: 200000 },
        { id: 'claude-3-haiku-20240307', name: 'Claude 3 Haiku', capabilities: ['text', 'vision'], context_length: 200000 },
        
        // Legacy Models
        { id: 'claude-2.1', name: 'Claude 2.1', capabilities: ['text'], context_length: 100000 },
        { id: 'claude-instant-1.2', name: 'Claude Instant 1.2', capabilities: ['text'], context_length: 100000 }
      ],
      openai: [
        { id: 'gpt-4o', name: 'GPT-4o', capabilities: ['text', 'vision'], context_length: 128000 },
        { id: 'gpt-4o-mini', name: 'GPT-4o Mini', capabilities: ['text', 'vision'], context_length: 128000 },
        { id: 'gpt-4', name: 'GPT-4', capabilities: ['text'], context_length: 8192 },
        { id: 'gpt-3.5-turbo', name: 'GPT-3.5 Turbo', capabilities: ['text'], context_length: 16385 }
      ],
      grok: [
        { id: 'grok-beta', name: 'Grok Beta', capabilities: ['text', 'real_time'], context_length: 131072 },
        { id: 'grok-2-1212', name: 'Grok 2', capabilities: ['text', 'real_time'], context_length: 131072 },
        { id: 'grok-2-vision-1212', name: 'Grok 2 Vision', capabilities: ['text', 'vision', 'real_time'], context_length: 131072 }
      ],
      gemini: [
        // Current Latest Models
        { id: 'gemini-1.5-flash', name: 'Gemini 1.5 Flash', capabilities: ['text', 'vision'], context_length: 1048576 },
        { id: 'gemini-1.5-pro', name: 'Gemini 1.5 Pro', capabilities: ['text', 'vision', 'audio'], context_length: 2097152 },
        
        // Legacy Models
        { id: 'gemini-pro', name: 'Gemini Pro (Deprecated)', capabilities: ['text'], context_length: 32768 },
        { id: 'gemini-pro-vision', name: 'Gemini Pro Vision (Deprecated)', capabilities: ['text', 'vision'], context_length: 16384 }
      ],
      cloudflare: [
        { id: '@cf/meta/llama-3.1-8b-instruct', name: 'Llama 3.1 8B Instruct', capabilities: ['text', 'efficient'], context_length: 131072 },
        { id: '@cf/meta/llama-4-scout-17b-16e-instruct', name: 'Llama 4 Scout 17B (Multimodal)', capabilities: ['text', 'vision', 'multimodal'], context_length: 16384 },
        { id: '@cf/google/gemma-3-12b-it', name: 'Gemma 3 12B (Multimodal)', capabilities: ['text', 'vision', 'multimodal'], context_length: 80000 },
        { id: '@cf/mistral-small-3.1-24b-instruct', name: 'Mistral Small 3.1 24B (Vision)', capabilities: ['text', 'vision'], context_length: 128000 },
        { id: '@cf/meta/llama-3.2-11b-vision-instruct', name: 'Llama 3.2 11B Vision Instruct', capabilities: ['text', 'vision'], context_length: 131072 },
        { id: '@cf/meta/llama-2-7b-chat-int8', name: 'Llama 2 7B Chat', capabilities: ['text', 'chat', 'efficient'], context_length: 4096 },
        { id: '@cf/mistral/mistral-7b-instruct-v0.1', name: 'Mistral 7B Instruct', capabilities: ['text', 'instruction_following'], context_length: 8192 },
        { id: '@cf/microsoft/phi-2', name: 'Microsoft Phi-2', capabilities: ['text', 'compact', 'edge_deployment'], context_length: 2048 },
        { id: '@cf/qwen/qwen1.5-7b-chat-awq', name: 'Qwen 1.5 7B Chat', capabilities: ['text', 'bilingual', 'chat'], context_length: 32768 },
        { id: '@cf/google/gemma-2b-it', name: 'Gemma 2B IT', capabilities: ['text', 'lightweight', 'edge_deployment'], context_length: 8192 }
      ],
      mistral: [
        { id: 'mistral-ocr-latest', name: 'Mistral OCR', capabilities: ['vision', 'ocr', 'document_understanding'], context_length: 32000 },
        { id: 'mistral-large-latest', name: 'Mistral Large', capabilities: ['text'], context_length: 128000 },
        { id: 'mistral-medium', name: 'Mistral Medium', capabilities: ['text'], context_length: 32768 },
        { id: 'mistral-small', name: 'Mistral Small', capabilities: ['text'], context_length: 32768 }
      ],
      deepseek: [
        { id: 'deepseek-chat', name: 'DeepSeek Chat (V3)', capabilities: ['text', 'chat', 'general_purpose'], context_length: 64000 },
        { id: 'deepseek-reasoner', name: 'DeepSeek Reasoner (R1)', capabilities: ['text', 'reasoning', 'math', 'coding'], context_length: 64000 },
        { id: 'deepseek-r1', name: 'DeepSeek-R1', capabilities: ['text', 'reasoning', 'chain_of_thought'], context_length: 32768 },
        { id: 'deepseek-vision', name: 'DeepSeek Vision', capabilities: ['text', 'vision'], context_length: 32768 }
      ]
    };

    return fallbackModels[provider] || [];
  }

  // Helper method to get formatted models for dropdown
  getFormattedModelsForDropdown(models: ModelInfo[]): Array<{ value: string; label: string; description?: string }> {
    return models.map(model => ({
      value: model.id,
      label: model.name,
      description: model.description
    }));
  }

  // Clear local cache
  clearCache(): void {
    this.cache.clear();
  }

  // Get provider availability from cache or API
  async getProviderAvailability(): Promise<Record<string, boolean>> {
    try {
      // First, try to get availability from the dedicated endpoint with cache busting
      const cacheBuster = `?t=${Date.now()}`;
      const response = await this.makeRequest(`/v1/providers/availability${cacheBuster}`);
      const data = response.data || response;
      
      if (data.providers) {
        const availability: Record<string, boolean> = {};
        
        // If we get an array of provider objects
        if (Array.isArray(data.providers)) {
          data.providers.forEach((p: any) => {
            availability[p.provider] = p.available || p.configured || false;
          });
        } 
        // If we get configured providers list
        else if (data.configuredProviders && Array.isArray(data.configuredProviders)) {
          const providers = ['anthropic', 'openai', 'grok', 'gemini', 'cloudflare', 'mistral', 'deepseek'];
          providers.forEach(provider => {
            availability[provider] = data.configuredProviders.includes(provider);
          });
        }
        
        // Always mark Cloudflare as available since it's built-in
        availability.cloudflare = true;
        
        return availability;
      }
    } catch (error) {
      console.error('Failed to get provider availability from API:', error);
    }
    
    // Fallback: try to get from models endpoint
    try {
      const allModels = await this.getAllProviderModels();
      const availability: Record<string, boolean> = {};
      
      for (const [provider, data] of Object.entries(allModels.providers)) {
        availability[provider] = data.available || false;
      }
      
      // Always mark Cloudflare as available since it's built-in
      availability.cloudflare = true;
      
      return availability;
    } catch (error) {
      console.error('Failed to get provider availability from models endpoint:', error);
    }
    
    // Final fallback: return default availability
    const defaultAvailability: Record<string, boolean> = {
      anthropic: false,
      openai: false,
      grok: false,
      gemini: false,
      cloudflare: true, // Always available
      mistral: false,
      deepseek: false
    };
    
    return defaultAvailability;
  }

  // Get available models for a specific provider
  async getAvailableModels(provider: string): Promise<ModelInfo[]> {
    try {
      // First try to get models from API
      const models = await this.getModelsForProvider(provider);
      
      // Ensure each model has the required properties
      return models.map(model => ({
        id: model.id,
        name: model.name,
        description: model.description || this.getModelDescription(provider, model.id),
        capabilities: model.capabilities || this.getModelCapabilities(provider, model.id),
        context_length: model.context_length,
        pricing: model.pricing
      }));
    } catch (error) {
      console.error(`Failed to get available models for ${provider}:`, error);
      
      // Return fallback models with enriched data
      const fallbackModels = this.getFallbackModels(provider);
      return fallbackModels.map(model => ({
        ...model,
        description: model.description || this.getModelDescription(provider, model.id),
        capabilities: model.capabilities || this.getModelCapabilities(provider, model.id)
      }));
    }
  }

  // Get model description based on provider and model ID
  private getModelDescription(provider: string, modelId: string): string {
    const descriptions: Record<string, Record<string, string>> = {
      anthropic: {
        // Claude 4 Series
        'claude-opus-4': 'Most powerful model and world\'s best coding model with extended thinking',
        'claude-sonnet-4': 'Superior coding and reasoning with hybrid thinking modes',
        
        // Claude 3.7 Series
        'claude-3-7-sonnet': 'Pioneering hybrid AI reasoning model with step-by-step thinking',
        
        // Claude 3.5 Series
        'claude-3-5-sonnet-20241022': 'Industry-leading intelligence with superior coding capabilities',
        'claude-3-5-sonnet-20240620': 'Previous version of Claude 3.5 Sonnet',
        'claude-3-5-haiku': 'Fastest model with improved performance across all skills',
        
        // Claude 3 Series
        'claude-3-opus-20240229': 'Most powerful Claude 3 model for highly complex tasks',
        'claude-3-sonnet-20240229': 'Balance of intelligence and speed',
        'claude-3-haiku-20240307': 'Fast model for simple tasks',
        
        // Legacy Models
        'claude-2.1': 'Previous generation Claude model',
        'claude-instant-1.2': 'Fast and lightweight Claude model'
      },
      openai: {
        'gpt-4-turbo-preview': 'Latest GPT-4 Turbo with vision capabilities and 128K context',
        'gpt-4': 'Most capable GPT-4 model for complex reasoning',
        'gpt-3.5-turbo': 'Fast and efficient for most tasks',
        'gpt-4.1-turbo': 'Enhanced GPT-4 Turbo with improved performance'
      },
      grok: {
        'grok-beta': 'Real-time AI with access to current information',
        'grok-2-1212': 'Second generation Grok model with enhanced reasoning',
        'grok-2-vision-1212': 'Grok 2 with vision and image understanding capabilities',
        'grok-2-image-1212': 'Grok 2 with advanced image processing capabilities',
        'grok-3': 'Flagship enterprise model for data extraction and reasoning',
        'grok-3-fast': 'Fast variant of Grok 3 for low-latency applications',
        'grok-3-mini': 'Lightweight model that excels at quantitative and reasoning tasks',
        'grok-3-mini-fast': 'Fast lightweight model for reasoning and math tasks',
        'grok-4-0709': 'Next generation Grok model in preview'
      },
      gemini: {
        'gemini-pro': 'Google\'s most capable text model',
        'gemini-pro-vision': 'Multimodal Gemini with vision capabilities',
        'gemini-2.5-flash': 'Fast and efficient Gemini model'
      },
      cloudflare: {
        // Latest Text Generation Models
        '@cf/meta/llama-4-scout-17b-16e-instruct': 'Advanced multimodal model with vision capabilities',
        '@cf/meta/llama-3.3-70b-instruct-fp8-fast': 'Large, fast instruction-following model',
        '@cf/meta/llama-3.1-8b-instruct-fast': 'Efficient instruction-following model',
        '@cf/google/gemma-3-12b-it': 'Multimodal model with 80K context window',
        '@cf/mistral/mistral-small-3.1-24b-instruct': 'Vision-capable Mistral with 128K context',
        '@cf/qwen/qwq-32b': 'Advanced reasoning and problem-solving model',
        '@cf/qwen/qwen2.5-coder-32b-instruct': 'Specialized coding and programming model',
        '@cf/meta/llama-guard-3-8b': 'Safety and content moderation model',
        '@cf/deepseek/deepseek-r1-distill-qwen-32b': 'Advanced reasoning model with chain-of-thought',
        '@cf/meta/llama-3.2-1b-instruct': 'Compact instruction-following model',
        '@cf/meta/llama-3.2-3b-instruct': 'Balanced instruction-following model',
        '@cf/meta/llama-3.2-11b-vision-instruct': 'Vision-enabled instruction model',
        
        // Legacy Models
        '@cf/meta/llama-2-7b-chat-int8': 'Efficient chat model based on Llama 2',
        '@cf/mistral/mistral-7b-instruct-v0.1': 'Instruction-tuned Mistral model',
        '@cf/microsoft/phi-2': 'Small but capable Microsoft model',
        '@cf/qwen/qwen1.5-7b-chat-awq': 'Advanced Chinese-English bilingual model',
        '@cf/google/gemma-2b-it': 'Lightweight Google model for edge deployment',
        
        // Embeddings Models
        '@cf/baai/bge-reranker-base': 'Text reranking and relevance scoring',
        '@cf/baai/bge-m3': 'Multilingual dense retrieval model',
        '@cf/baai/bge-large-en-v1.5': 'Large English text embeddings',
        '@cf/baai/bge-small-en-v1.5': 'Compact English text embeddings',
        '@cf/baai/bge-base-en-v1.5': 'Balanced English text embeddings',
        
        // Classification Models
        '@cf/huggingface/distilbert-sst-2-int8': 'Sentiment analysis and classification',
        
        // Vision/Image Models
        '@cf/black-forest-labs/flux-1-schnell': 'Fast text-to-image generation',
        '@cf/llava-hf/llava-1.5-7b-hf': 'Large language and vision assistant',
        '@cf/unum/uform-gen2-qwen-500m': 'Lightweight multimodal model',
        '@cf/facebook/detr-resnet-50': 'Object detection and recognition',
        '@cf/bytedance/stable-diffusion-xl-lightning': 'Fast high-quality image generation',
        '@cf/lykon/dreamshaper-8-lcm': 'Artistic image generation model',
        '@cf/runwayml/stable-diffusion-v1-5-img2img': 'Image-to-image transformation',
        '@cf/runwayml/stable-diffusion-v1-5': 'Classic text-to-image generation'
      },
      mistral: {
        'mistral-tiny': 'Smallest and fastest Mistral model',
        'mistral-small': 'Balanced Mistral model for general use',
        'mistral-medium': 'More capable Mistral for complex tasks',
        'mixtral-8x7b-instruct': 'Mixture of experts model with superior performance'
      },
      deepseek: {
        'deepseek-chat': 'General-purpose conversational AI model based on DeepSeek-V3',
        'deepseek-reasoner': 'Advanced reasoning model for math, coding, and complex problem solving',
        'deepseek-coder': 'Specialized model for code generation (legacy)',
        'deepseek-r1': 'Open-source reasoning model with chain-of-thought capabilities',
        'deepseek-r1-distill-qwen-32b': 'Distilled reasoning model based on Qwen architecture',
        'deepseek-r1-distill-qwen-14b': 'Compact distilled reasoning model',
        'deepseek-r1-distill-qwen-7b': 'Lightweight distilled reasoning model'
      }
    };

    return descriptions[provider]?.[modelId] || 'Advanced AI model for threat analysis';
  }

  // Get model capabilities based on provider and model ID
  private getModelCapabilities(provider: string, modelId: string): string[] {
    const capabilities: Record<string, Record<string, string[]>> = {
      anthropic: {
        // Claude 4 Series
        'claude-opus-4': ['text', 'vision', 'tool_use', 'computer_use', 'extended_thinking', 'coding', 'reasoning'],
        'claude-sonnet-4': ['text', 'vision', 'tool_use', 'computer_use', 'extended_thinking', 'coding', 'reasoning'],
        
        // Claude 3.7 Series
        'claude-3-7-sonnet': ['text', 'vision', 'tool_use', 'extended_thinking', 'reasoning', 'step_by_step'],
        
        // Claude 3.5 Series
        'claude-3-5-sonnet-20241022': ['text', 'vision', 'tool_use', 'computer_use', 'coding'],
        'claude-3-5-sonnet-20240620': ['text', 'vision', 'tool_use', 'analysis'],
        'claude-3-5-haiku': ['text', 'vision', 'tool_use', 'fast_response'],
        
        // Claude 3 Series
        'claude-3-opus-20240229': ['text', 'vision', 'analysis', 'reasoning'],
        'claude-3-sonnet-20240229': ['text', 'vision', 'analysis'],
        'claude-3-haiku-20240307': ['text', 'vision', 'fast_response'],
        
        // Legacy Models
        'claude-2.1': ['text', 'analysis'],
        'claude-instant-1.2': ['text', 'fast_response']
      },
      openai: {
        'gpt-4-turbo-preview': ['text', 'vision', 'analysis', '128k_context'],
        'gpt-4': ['text', 'analysis', 'reasoning'],
        'gpt-3.5-turbo': ['text', 'fast_response'],
        'gpt-4.1-turbo': ['text', 'vision', 'analysis', 'enhanced_performance']
      },
      grok: {
        'grok-beta': ['text', 'real_time', 'current_events'],
        'grok-2-1212': ['text', 'real_time', 'reasoning'],
        'grok-2-vision-1212': ['text', 'vision', 'real_time', 'image_understanding'],
        'grok-2-image-1212': ['text', 'vision', 'image_processing', 'real_time'],
        'grok-3': ['text', 'reasoning', 'enterprise', 'data_extraction'],
        'grok-3-fast': ['text', 'reasoning', 'fast', 'low_latency'],
        'grok-3-mini': ['text', 'reasoning', 'math', 'quantitative', 'compact'],
        'grok-3-mini-fast': ['text', 'reasoning', 'math', 'fast', 'compact'],
        'grok-4-0709': ['text', 'reasoning', 'advanced', 'preview', 'next_generation']
      },
      gemini: {
        'gemini-pro': ['text', 'analysis', 'reasoning'],
        'gemini-pro-vision': ['text', 'vision', 'analysis'],
        'gemini-2.5-flash': ['text', 'fast_response', 'efficient']
      },
      cloudflare: {
        // Latest Text Generation Models
        '@cf/meta/llama-4-scout-17b-16e-instruct': ['text', 'vision', 'multimodal'],
        '@cf/meta/llama-3.3-70b-instruct-fp8-fast': ['text', 'reasoning', 'fast'],
        '@cf/meta/llama-3.1-8b-instruct-fast': ['text', 'efficient', 'fast'],
        '@cf/google/gemma-3-12b-it': ['text', 'vision', 'multimodal', '80k_context'],
        '@cf/mistral/mistral-small-3.1-24b-instruct': ['text', 'vision', '128k_context'],
        '@cf/qwen/qwq-32b': ['text', 'reasoning', 'math', 'problem_solving'],
        '@cf/qwen/qwen2.5-coder-32b-instruct': ['text', 'code_generation', 'programming'],
        '@cf/meta/llama-guard-3-8b': ['text', 'safety', 'moderation'],
        '@cf/deepseek/deepseek-r1-distill-qwen-32b': ['text', 'reasoning', 'chain_of_thought'],
        '@cf/meta/llama-3.2-1b-instruct': ['text', 'compact', 'efficient'],
        '@cf/meta/llama-3.2-3b-instruct': ['text', 'balanced'],
        '@cf/meta/llama-3.2-11b-vision-instruct': ['text', 'vision'],
        
        // Legacy Models
        '@cf/meta/llama-2-7b-chat-int8': ['text', 'chat', 'efficient'],
        '@cf/mistral/mistral-7b-instruct-v0.1': ['text', 'instruction_following'],
        '@cf/microsoft/phi-2': ['text', 'compact', 'edge_deployment'],
        '@cf/qwen/qwen1.5-7b-chat-awq': ['text', 'bilingual', 'chat'],
        '@cf/google/gemma-2b-it': ['text', 'lightweight', 'edge_deployment'],
        
        // Embeddings Models
        '@cf/baai/bge-reranker-base': ['embeddings', 'reranking'],
        '@cf/baai/bge-m3': ['embeddings', 'multilingual'],
        '@cf/baai/bge-large-en-v1.5': ['embeddings'],
        '@cf/baai/bge-small-en-v1.5': ['embeddings', 'compact'],
        '@cf/baai/bge-base-en-v1.5': ['embeddings', 'balanced'],
        
        // Classification Models
        '@cf/huggingface/distilbert-sst-2-int8': ['text', 'classification', 'sentiment'],
        
        // Vision/Image Models
        '@cf/black-forest-labs/flux-1-schnell': ['text_to_image', 'generation', 'fast'],
        '@cf/llava-hf/llava-1.5-7b-hf': ['vision', 'text', 'multimodal'],
        '@cf/unum/uform-gen2-qwen-500m': ['vision', 'text', 'multimodal', 'lightweight'],
        '@cf/facebook/detr-resnet-50': ['vision', 'object_detection'],
        '@cf/bytedance/stable-diffusion-xl-lightning': ['text_to_image', 'generation', 'fast'],
        '@cf/lykon/dreamshaper-8-lcm': ['text_to_image', 'generation', 'artistic'],
        '@cf/runwayml/stable-diffusion-v1-5-img2img': ['image_to_image', 'transformation'],
        '@cf/runwayml/stable-diffusion-v1-5': ['text_to_image', 'generation']
      },
      mistral: {
        'mistral-tiny': ['text', 'fast_response'],
        'mistral-small': ['text', 'balanced'],
        'mistral-medium': ['text', 'analysis'],
        'mixtral-8x7b-instruct': ['text', 'moe', 'analysis']
      },
      deepseek: {
        'deepseek-chat': ['text', 'chat', 'general_purpose'],
        'deepseek-reasoner': ['text', 'reasoning', 'math', 'coding', 'chain_of_thought'],
        'deepseek-coder': ['text', 'code_generation'],
        'deepseek-r1': ['text', 'reasoning', 'chain_of_thought', 'open_source'],
        'deepseek-r1-distill-qwen-32b': ['text', 'reasoning', 'distilled', 'efficient'],
        'deepseek-r1-distill-qwen-14b': ['text', 'reasoning', 'distilled', 'compact'],
        'deepseek-r1-distill-qwen-7b': ['text', 'reasoning', 'distilled', 'lightweight']
      }
    };

    return capabilities[provider]?.[modelId] || ['text', 'analysis'];
  }
}

export const modelsService = new ModelsService();